import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { LineChartPoint, ReferenceLine } from '../patient-chart.component';

interface DisplayPoint {
  color: string;
  title: string;
  score: string;
  units: string;
  severity: string;
}

@Component({
  selector: 'csi-patient-chart-series-tooltip',
  templateUrl: './patient-chart-series-tooltip.component.html',
  styleUrls: ['./patient-chart-series-tooltip.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PatientChartSeriesTooltipComponent implements OnInit, OnChanges, OnDestroy {
  @Input() points: (LineChartPoint & { color: string })[] = [];
  @Input() referenceLines: ReferenceLine[] = [];

  displayPoints: DisplayPoint[] = [];
  private translations: { [key: string]: string } = {};
  private sub: Subscription;

  constructor(private transloco: TranslocoService) {}

  ngOnInit() {
    this.sub = this.transloco.selectTranslateObject('PATIENT_CHART').subscribe(map => {
      this.translations = map;
      this.recompute();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['referenceLines'] && this.referenceLines) {
      this.referenceLines.sort((a, b) => b.value - a.value);
    }

    if (changes['referenceLines'] || changes['points']) {
      this.recompute();
    }
  }

  ngOnDestroy() {
    if (this.sub) this.sub.unsubscribe();
  }

  private recompute() {
    if (!this.points.length || !this.translations) {
      this.displayPoints = [];
      return;
    }

    this.displayPoints = this.points.map(pt => {
      const title = this.translations[pt.series] || pt.series;

      const rawScore = String(pt.displayValue);
      const score = this.translations[rawScore] || rawScore;

      const rawUnits = pt.units || '';
      const units = this.translations[rawUnits] || rawUnits;

      const rawSeverity = this.toSeverity(pt.value);
      const severity =
        rawSeverity && this.translations[rawSeverity] != null
          ? this.translations[rawSeverity]
          : rawSeverity;

      return { color: pt.color, title, score, units, severity };
    });
  }

  public toSeverity(normalizedValue: number): string {
    if (this.referenceLines) {
      const refLine = this.referenceLines
        .slice(1)
        .find((referenceLine: ReferenceLine) => normalizedValue >= referenceLine.value);
      return !!refLine ? refLine.name : normalizedValue.toString();
    }
  }
}
